import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';

const AdminAuthContext = createContext();

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};

export const AdminAuthProvider = ({ children }) => {
  const [adminUser, setAdminUser] = useState(null);
  const [adminToken, setAdminToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check for existing admin session on component mount
  useEffect(() => {
    const checkAdminSession = () => {
      try {
        const storedAdminToken = localStorage.getItem('adminAuthToken');
        const storedAdminUser = localStorage.getItem('adminAuthUser');

        if (storedAdminToken && storedAdminUser) {
          const parsedUser = JSON.parse(storedAdminUser);
          // Verify user is admin type
          if (parsedUser.userType === 'admin') {
            setAdminToken(storedAdminToken);
            setAdminUser(parsedUser);
          } else {
            // Clear invalid admin session
            localStorage.removeItem('adminAuthToken');
            localStorage.removeItem('adminAuthUser');
          }
        }
      } catch (error) {
        console.error('Error checking admin session:', error);
        // Clear invalid session data
        localStorage.removeItem('adminAuthToken');
        localStorage.removeItem('adminAuthUser');
      } finally {
        setLoading(false);
      }
    };

    checkAdminSession();
  }, []);

  const adminLogin = async (email, password) => {
    try {
      const response = await authAPI.login({ email, password, rememberMe: true });
      
      if (response.success && response.user.userType === 'admin') {
        setAdminToken(response.token);
        setAdminUser(response.user);
        
        // Store admin session
        localStorage.setItem('adminAuthToken', response.token);
        localStorage.setItem('adminAuthUser', JSON.stringify(response.user));
        
        return { success: true, user: response.user };
      } else if (response.success && response.user.userType !== 'admin') {
        return { success: false, message: 'Accesso negato. Account non autorizzato per l\'area admin.' };
      } else {
        return { success: false, message: response.message || 'Credenziali non valide' };
      }
    } catch (error) {
      console.error('Admin login error:', error);
      return { 
        success: false, 
        message: error.response?.data?.detail || 'Errore durante l\'accesso' 
      };
    }
  };

  const adminLogout = async () => {
    try {
      if (adminToken) {
        // Call logout API
        await authAPI.logout(adminToken);
      }
    } catch (error) {
      console.error('Admin logout API error:', error);
    } finally {
      // Clear local state and storage regardless of API call result
      setAdminToken(null);
      setAdminUser(null);
      localStorage.removeItem('adminAuthToken');
      localStorage.removeItem('adminAuthUser');
    }
  };

  const isAdminAuthenticated = () => {
    return !!(adminUser && adminToken && adminUser.userType === 'admin');
  };

  const value = {
    adminUser,
    adminToken,
    adminAuthToken: adminToken, // Alias for compatibility
    adminLogin,
    adminLogout,
    isAdminAuthenticated,
    loading
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};